import React, { useEffect, useState } from "react";
import { Spinner, Badge, Button, Dropdown, Nav } from "react-bootstrap";
import { toast } from "sonner";
import styles from "./outlook.module.css";
import imgs from "../../../Utils/assets/img/faces/5.jpg";
import ComposeModal from "./ComposeModal";
import {
  faEllipsisVertical,
  faEnvelope,
  faGlobe,
  faMailForward,
  faRefresh,
  faReply,
  faReplyAll,
  faRightFromBracket,
  faServer,
  faTrash,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DeleteModal from "../../DeleteModal";
import { formatDate, formatTime } from "../../../Utils/helpers/commonhelper";
import { baseURL } from "../../../Utils/constants/constant";
import { PostRequest } from "../../../Utils/hooks/ApiHandler";
import { useNavigate } from "react-router-dom";
import useLocalStorageState from "use-local-storage-state";
import { logoutOutlook } from "../../Calendar/outlookClient";

export default function OutlookMails() {
  const navigate = useNavigate();
  const [customer, setCustomer] = useLocalStorageState("customer");
  const [isOutlookLoggedIn, setIsOutlookLoggedIn] = useState(
    () => localStorage.getItem("outlook_connected") === "true",
  );
  const [folder, setFolder] = useState("inbox");
  const [mails, setMails] = useState([]);
  const [selectedMail, setSelectedMail] = useState(null);
  const [threadMails, setThreadMails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [threadLoading, setThreadLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeType, setComposeType] = useState("new");
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteModal1, setShowDeleteModal1] = useState(false);
  const [designationToDelete, setDesignationToDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [collapsedIds, setCollapsedIds] = useState({});
  const [previewImage, setPreviewImage] = useState(null);

  const userEmail = sessionStorage.getItem("outlook_email");
  const userName = sessionStorage.getItem("outlook_name");
  const service = sessionStorage.getItem("email_service");
  const loadMails = async () => {
    try {
      setLoading(true);
      setSelectedMail(null);
      setThreadMails([]);

      const res = await PostRequest(
        `${baseURL}inbox/mails`,
        { user_id: customer?.customer_details?.id, folder },
        false,
        {},
        {},
      );
      setMails(res?.data || []);
    } catch (err) {
      console.error("Mail load failed", err);
      toast.error("Failed to load mails");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMails();
  }, [folder]);

  // Listen for mail-sent event to refresh list
  useEffect(() => {
    const handler = () => loadMails();
    window.addEventListener("mail-sent", handler);
    return () => window.removeEventListener("mail-sent", handler);
  }, [folder]);

  /* ============ OPEN THREAD ============ */
  const openThread = async (mail) => {
    try {
      setSelectedMail(mail);
      setThreadMails([]);
      setThreadLoading(true);
      setCollapsedIds({});

      const res = await PostRequest(
        `${baseURL}inbox/conversation`,
        {
          user_id: customer?.customer_details?.id,
          conversationId: mail?.conversationId,
        },
        false,
        {},
        {},
      );

      setThreadMails(res?.data || []);
    } catch (err) {
      console.error("Thread load failed", err);
      toast.error("Failed to load conversation");
    } finally {
      setThreadLoading(false);
    }
  };

  /* ============ DELETE ============ */
  const onDelete = (ids) => {
    setDesignationToDelete(ids);
    setShowDeleteModal1(true);
  };

  const deleteMails = async (ids) => {
    try {
      setDeleting(true);
      await PostRequest(
        `${baseURL}inbox/delete`,
        { user_id: customer?.customer_details?.id, ids },
        false,
        {},
        {},
      );
      toast.success("Deleted successfully");
      setShowDeleteModal1(false);
      setShowDeleteModal(false);
      setDesignationToDelete(null);
      setSelectedIds([]);
      if (selectedMail && ids.includes(selectedMail.id)) {
        setSelectedMail(null);
        setThreadMails([]);
      }
      await loadMails();
    } catch (err) {
      console.error("Delete failed", err);
      toast.error("Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  /* ============ REPLY RECIPIENTS ============ */
  const getReplyRecipients = (type) => {
    if (!selectedMail) return [];
    const me = (userEmail || "").toLowerCase();
    const from = selectedMail?.from?.emailAddress?.address || "";
    const to =
      selectedMail?.toRecipients
        ?.map((r) => r?.emailAddress?.address || "")
        .filter(Boolean) || [];
    const cc =
      selectedMail?.ccRecipients
        ?.map((r) => r?.emailAddress?.address || "")
        .filter(Boolean) || [];

    if (type === "reply") return from ? [from] : [];
    if (type === "replyAll") {
      const all = [from, ...to, ...cc];
      return [...new Set(all)].filter((e) => e.toLowerCase() !== me);
    }
    return [];
  };

  const getOriginalMessage = () => {
    if (!threadMails || !threadMails.length) return null;
    return threadMails[threadMails.length - 1];
  };

  /* ============ COMPOSE OPEN ============ */
  const handleCompose = (type) => {
    setComposeType(type);
    setComposeOpen(true);
  };

  /* ============ DISCONNECT ============ */
  const handleDisconnect = async () => {
    const userId = customer?.customer_details?.id;
    await logoutOutlook(userId);
    localStorage.removeItem("outlook_connected");
    sessionStorage.removeItem("outlook_email");
    sessionStorage.removeItem("outlook_name");
    sessionStorage.removeItem("email_service");
    setIsOutlookLoggedIn(false);
    toast.success("Outlook disconnected");
    navigate("/email-settings");
  };

  /* ============ TOGGLE COLLAPSE ============ */
  const toggleCollapse = (id) => {
    setCollapsedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  /* ============ FILTERED MAILS ============ */
  const filteredMails = mails.filter((m) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (m.subject || "").toLowerCase().includes(q) ||
      (m.from?.emailAddress?.address || "").toLowerCase().includes(q) ||
      (m.bodyPreview || "").toLowerCase().includes(q)
    );
  });

  /* ============ FORMAT FILE SIZE ============ */
  const formatBytes = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  return (
    <>
      {/* ── COMPOSE MODAL ── */}
      <ComposeModal
        open={composeOpen}
        type={composeType}
        mail={selectedMail}
        originalMail={getOriginalMessage()}
        recipients={getReplyRecipients(composeType)}
        onClose={() => setComposeOpen(false)}
      />

      {/* ── DELETE MODALS ── */}
      <DeleteModal
        show={showDeleteModal1}
        onClose={() => !deleting && setShowDeleteModal1(false)}
        onConfirm={() => deleteMails(designationToDelete)}
        loading={deleting}
        message="Are you sure you want to delete this email?"
      />
      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => deleteMails(selectedIds)}
        loading={deleting}
        message="Are you sure you want to delete all selected emails?"
      />

      {/* ── IMAGE PREVIEW OVERLAY ── */}
      {previewImage && (
        <div
          onClick={() => setPreviewImage(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              maxWidth: "80vw",
              maxHeight: "90vh",
              overflow: "auto",
              padding: 20,
            }}
          >
            <img
              src={previewImage}
              alt="Preview"
              style={{ maxWidth: "100%", maxHeight: "80vh", borderRadius: 8 }}
            />
            <div
              style={{
                position: "absolute",
                top: "0.5rem",
                right: "0.5rem",
                display: "flex",
                gap: "0.5rem",
              }}
            >
              <a
                href={previewImage}
                download
                className="btn btn-sm btn-light rounded-circle shadow"
                title="Download"
              >
                ⬇️
              </a>
              <button
                className="btn btn-sm btn-light rounded-circle shadow"
                title="Close"
                onClick={() => setPreviewImage(null)}
              >
                ✖
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className="d-flex"
        style={{ height: "90vh", overflow: "hidden", maxHeight: "90vh" }}
      >
        <div
          className={styles.container}
          style={{ height: "100%", overflow: "hidden" }}
        >
          {/* ═══════════ LEFT SIDEBAR ═══════════ */}
          <div
            className="d-flex flex-column bg-white border"
            style={{
              width: "16%",
              minWidth: 160,
              height: "100%",
              overflow: "hidden",
            }}
          >
            <div className="p-3 flex-grow-1 overflow-auto">
              <button
                className="btn btn-success w-100 fw-semibold"
                onClick={() => handleCompose("new")}
              >
                + Compose Mail
              </button>
              <hr />
              <div className="text-muted fw-semibold small mb-2 ps-1">
                FOLDERS
              </div>
              <div className="nav flex-column gap-1">
                {[
                  {
                    key: "all",
                    label: "All Mails",
                    badge: "bg-success-subtle text-success",
                  },
                  {
                    key: "inbox",
                    label: "Inbox",
                    badge: "bg-primary-subtle text-primary",
                  },
                  {
                    key: "sent",
                    label: "Sent",
                    badge: "bg-secondary-subtle text-secondary",
                    icon: "✈️",
                  },
                ].map((item) => (
                  <button
                    key={item.key}
                    className={`nav-link d-flex align-items-center justify-content-between px-2 py-2 rounded ${
                      folder === item.key
                        ? "active bg-primary-subtle text-primary"
                        : "text-secondary"
                    }`}
                    style={{ border: "none", background: "none", fontSize: 13 }}
                    onClick={() => setFolder(item.key)}
                  >
                    <span className="d-flex align-items-center gap-2">
                      {item.icon && (
                        <span
                          style={{
                            transform: "rotate(-45deg)",
                            display: "inline-block",
                          }}
                        >
                          {item.icon}
                        </span>
                      )}
                      {item.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="p-3 border-top flex-shrink-0">
              <div className="mb-3 d-flex flex-column gap-2 text-muted small">
                {/* User Name */}
                <div className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon
                    icon={faUser}
                    className="text-secondary"
                    style={{ width: "16px", textAlign: "center" }}
                  />
                  <span className="fw-semibold text-dark text-truncate">
                    {userName || "saurabh"}
                  </span>
                </div>

                {/* User Email */}
                <div className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon
                    icon={faEnvelope}
                    className="text-secondary"
                    style={{ width: "16px", textAlign: "center" }}
                  />
                  <span className="text-truncate" title={userEmail}>
                    {userEmail || "email@example.com"}
                  </span>
                </div>

                {/* Service Name */}
                <div className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon
                    icon={faGlobe}
                    className="text-secondary"
                    style={{ width: "16px", textAlign: "center" }}
                  />
                  <span className="badge bg-light text-dark border text-truncate">
                    {service || "Gmail Service"}
                  </span>
                </div>
              </div>

              {/* Log Out Button */}
              <button
                className="btn btn-danger w-100 fw-semibold d-flex align-items-center justify-content-center gap-2"
                onClick={handleDisconnect}
              >
                <FontAwesomeIcon icon={faRightFromBracket} />
                <span>Log Out</span>
              </button>
            </div>
          </div>

          {/* ═══════════ MIDDLE: MAIL LIST ═══════════ */}
          <div
            className="d-flex flex-column bg-white border"
            style={{ width: "32%", height: "100%", overflow: "hidden" }}
          >
            {/* Header */}
            <div className="p-3 d-flex align-items-center justify-content-between border-bottom">
              <div className="d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={
                    mails.length > 0 && selectedIds.length === mails.length
                  }
                  onChange={(e) => {
                    setSelectedIds(
                      e.target.checked ? mails.map((m) => m.id) : [],
                    );
                  }}
                />
                <span
                  className="fw-semibold text-capitalize"
                  style={{ fontSize: 14 }}
                >
                  {folder === "sent"
                    ? "Sent"
                    : folder === "inbox"
                      ? "Inbox"
                      : "All Mails"}
                </span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={loadMails}
                  title="Refresh"
                >
                  <FontAwesomeIcon icon={faRefresh} />
                </button>
                {selectedIds.length > 0 && (
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => setShowDeleteModal(true)}
                    title="Delete Selected"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </div>
            </div>

            {/* Search */}
            <div className="px-3 py-2 border-bottom">
              <div className="d-flex gap-2">
                <input
                  className="form-control form-control-sm"
                  placeholder="Search emails..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setSearch("")}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {/* Mail List */}
            <div className="flex-grow-1 overflow-auto">
              {loading ? (
                <div className="p-4 text-center text-muted">
                  <div className="spinner-border spinner-border-sm me-2" />
                  Loading…
                </div>
              ) : !filteredMails.length ? (
                <p className="text-muted text-center my-5">No emails found</p>
              ) : (
                filteredMails.map((mail) => {
                  const isSent =
                    mail.from?.emailAddress?.address?.toLowerCase() ===
                    userEmail?.toLowerCase();
                  const isActive = selectedMail?.id === mail.id;
                  const isUnread = !mail.isRead;

                  return (
                    <div
                      key={mail.id}
                      className={`px-3 py-2 border-bottom ${isActive ? "bg-primary-subtle" : ""}`}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="d-flex align-items-start gap-2 w-100">
                        {/* Checkbox */}
                        <input
                          type="checkbox"
                          className="form-check-input mt-1 flex-shrink-0"
                          checked={selectedIds.includes(mail.id)}
                          onChange={() =>
                            setSelectedIds((prev) =>
                              prev.includes(mail.id)
                                ? prev.filter((i) => i !== mail.id)
                                : [...prev, mail.id],
                            )
                          }
                        />

                        {/* Avatar */}
                        <div
                          className="flex-shrink-0"
                          style={{ width: 36, height: 36 }}
                          onClick={() => openThread(mail)}
                        >
                          <img
                            src={imgs}
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                            }}
                            alt=""
                          />
                        </div>

                        {/* Mail content */}
                        <div
                          className="flex-grow-1 overflow-hidden"
                          onClick={() => openThread(mail)}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-1">
                            <strong
                              className="text-truncate"
                              style={{
                                maxWidth: "65%",
                                fontSize: 12,
                                fontWeight: isUnread ? 700 : 500,
                                color: "#333",
                              }}
                            >
                              {mail.from?.emailAddress?.address || "Unknown"}
                            </strong>
                            <small
                              className="text-muted flex-shrink-0"
                              style={{ fontSize: 11 }}
                            >
                              {formatTime(
                                new Date(mail?.sentDateTime).toLocaleTimeString(
                                  [],
                                  {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  },
                                ),
                              )}
                            </small>
                          </div>

                          <div
                            className="text-truncate"
                            style={{
                              fontSize: 13,
                              fontWeight: isUnread ? 600 : 400,
                              color: "#1a1a1a",
                            }}
                          >
                            {mail.subject
                              ? mail.subject.replace(/^PUG_CRM\s*/i, "")
                              : "(No Subject)"}
                          </div>

                          <div
                            className="text-muted text-truncate"
                            style={{ fontSize: 12 }}
                          >
                            {mail.bodyPreview || ""}
                          </div>

                          <div className="d-flex align-items-center gap-1 mt-1">
                            <span
                              className={`badge ${
                                isSent
                                  ? "bg-primary-subtle text-primary"
                                  : "bg-success-subtle text-success"
                              }`}
                              style={{ fontSize: 10 }}
                            >
                              {isSent ? "Sent" : "Inbox"}
                            </span>
                            {mail.hasAttachments && (
                              <span
                                className="text-muted"
                                style={{ fontSize: 11 }}
                              >
                                📎
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Delete button */}
                        <button
                          className="btn btn-sm flex-shrink-0"
                          title="Delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete([mail.id]);
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faTrash}
                            className="text-danger"
                            style={{ fontSize: 12 }}
                          />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* ═══════════ RIGHT: THREAD VIEW ═══════════ */}
          <div
            className="d-flex flex-column"
            style={{ width: "52%", height: "100%", overflow: "hidden" }}
          >
            <div
              className="bg-white border d-flex flex-column"
              style={{ height: "100%", overflow: "hidden" }}
            >
              {!selectedMail ? (
                <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted">
                  <div
                    className="rounded-circle bg-secondary-subtle mb-3 d-flex align-items-center justify-content-center"
                    style={{ width: 56, height: 56, fontSize: 24 }}
                  >
                    ✉️
                  </div>
                  <p className="mb-0">Select an email to read</p>
                </div>
              ) : threadLoading ? (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <div className="spinner-border text-primary" />
                </div>
              ) : (
                <>
                  {/* Thread header */}
                  <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-start flex-shrink-0">
                    <div className="d-flex align-items-center gap-3">
                      <img
                        src={imgs}
                        alt=""
                        style={{ width: 40, height: 40, borderRadius: "50%" }}
                      />
                      <div>
                        <h6 className="mb-0" style={{ fontSize: 15 }}>
                          {selectedMail?.from?.emailAddress?.address ||
                            "Unknown Sender"}
                        </h6>
                        <small className="text-muted">
                          {(selectedMail?.subject || "")
                            .replace(/\bPUG_CRM\b/gi, "")
                            .replace(/^(Re:|Fwd:)+\s*/gi, "")
                            .trim() || "(No Subject)"}
                        </small>
                        {(selectedMail?.toRecipients?.length > 0 ||
                          selectedMail?.ccRecipients?.length > 0) && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "#888",
                              marginTop: 2,
                            }}
                          >
                            <span>
                              To:{" "}
                              {selectedMail.toRecipients
                                ?.map((r) => r.emailAddress.address)
                                .join(", ")}
                            </span>
                            {selectedMail?.ccRecipients?.length > 0 && (
                              <span className="ms-2">
                                · Cc:{" "}
                                {selectedMail.ccRecipients
                                  ?.map((r) => r.emailAddress.address)
                                  .join(", ")}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions dropdown */}
                    <Dropdown>
                      <Dropdown.Toggle as={Nav.Link} className="p-1">
                        <FontAwesomeIcon icon={faEllipsisVertical} />
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="dropdown-menu-end">
                        <Dropdown.Item onClick={() => handleCompose("reply")}>
                          <FontAwesomeIcon icon={faReply} className="me-2" />
                          Reply
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => handleCompose("replyAll")}
                        >
                          <FontAwesomeIcon icon={faReplyAll} className="me-2" />
                          Reply All
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleCompose("forward")}>
                          <FontAwesomeIcon
                            icon={faMailForward}
                            className="me-2"
                          />
                          Forward
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          className="text-danger"
                          onClick={() => onDelete([selectedMail.id])}
                        >
                          <FontAwesomeIcon icon={faTrash} className="me-2" />
                          Delete
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>

                  {/* Thread messages */}
                  <div className="flex-grow-1 overflow-auto p-3">
                    {threadMails.length === 0 ? (
                      <div className="border rounded p-3 bg-light">
                        <div className="d-flex justify-content-between small text-muted mb-2">
                          <span>
                            {selectedMail?.from?.emailAddress?.address}
                          </span>
                          <span>
                            {new Date(
                              selectedMail?.sentDateTime,
                            ).toLocaleString()}
                          </span>
                        </div>
                        <div
                          style={{ fontSize: 13 }}
                          dangerouslySetInnerHTML={{
                            __html: selectedMail?.bodyPreview || "",
                          }}
                        />
                      </div>
                    ) : (
                      threadMails.map((msg, i) => {
                        const isCollapsed = collapsedIds[msg.id] ?? false;

                        return (
                          <div
                            key={msg.id}
                            className="border rounded mb-3 overflow-hidden"
                          >
                            {/* Message header — clickable to collapse */}
                            <div
                              className="d-flex justify-content-between align-items-center px-3 py-2 bg-light border-bottom"
                              style={{ cursor: "pointer" }}
                              onClick={() => toggleCollapse(msg.id)}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <img
                                  src={imgs}
                                  alt=""
                                  style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                  }}
                                />
                                <div>
                                  <div
                                    style={{ fontSize: 13, fontWeight: 600 }}
                                  >
                                    {msg.from?.emailAddress?.address ||
                                      "Unknown"}
                                  </div>
                                  {isCollapsed && (
                                    <div
                                      className="text-muted text-truncate"
                                      style={{ fontSize: 11, maxWidth: 300 }}
                                    >
                                      {msg.bodyPreview || ""}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <small
                                  className="text-muted"
                                  style={{ fontSize: 11 }}
                                >
                                  {formatDate(
                                    new Date(
                                      msg.sentDateTime || msg.receivedDateTime,
                                    ),
                                  )}{" "}
                                  {formatTime(
                                    new Date(
                                      msg.sentDateTime || msg.receivedDateTime,
                                    ).toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    }),
                                  )}
                                </small>
                                <span style={{ fontSize: 11, color: "#aaa" }}>
                                  {isCollapsed ? "▼" : "▲"}
                                </span>
                              </div>
                            </div>

                            {/* Message body — collapsible */}
                            {!isCollapsed && (
                              <div className="p-3 border-top flex-shrink-0">
                                {/* To / CC info */}
                                {(msg.toRecipients?.length > 0 ||
                                  msg.ccRecipients?.length > 0) && (
                                  <div
                                    className="mb-2 text-muted"
                                    style={{ fontSize: 11 }}
                                  >
                                    {msg.toRecipients?.length > 0 && (
                                      <span>
                                        To:{" "}
                                        {msg.toRecipients
                                          .map((r) => r.emailAddress.address)
                                          .join(", ")}
                                      </span>
                                    )}
                                    {msg.ccRecipients?.length > 0 && (
                                      <span className="ms-2">
                                        · Cc:{" "}
                                        {msg.ccRecipients
                                          .map((r) => r.emailAddress.address)
                                          .join(", ")}
                                      </span>
                                    )}
                                  </div>
                                )}

                                {/* Email body */}
                                <div
                                  className="email-content"
                                  style={{ fontSize: 13, lineHeight: 1.6 }}
                                  dangerouslySetInnerHTML={{
                                    __html:
                                      msg.body?.content ||
                                      msg.bodyPreview ||
                                      "",
                                  }}
                                />

                                {msg.attachments.map((att, idx) => {
                                  const filename = att.name || att.filename;
                                  const contentType =
                                    att.contentType || att.mime || "";
                                  const dataUrl =
                                    att.dataUrl ||
                                    (att.contentBytes
                                      ? `data:${contentType};base64,${att.contentBytes}`
                                      : "#");
                                  if (contentType.startsWith("image/")) {
                                    return (
                                      <div
                                        key={idx}
                                        className="border rounded overflow-hidden"
                                        style={{ maxWidth: "100%" }}
                                      >
                                        <img
                                          src={dataUrl}
                                          alt={filename}
                                          style={{
                                            maxWidth: "100%",
                                            maxHeight: 220,
                                            objectFit: "cover",
                                            display: "block",
                                            cursor: "pointer",
                                          }}
                                          onClick={() =>
                                            setPreviewImage(dataUrl)
                                          }
                                        />
                                        <div className="p-2 bg-light d-flex align-items-center justify-content-between">
                                          <span
                                            className="text-truncate"
                                            style={{
                                              fontSize: 11,
                                              maxWidth: 160,
                                            }}
                                          >
                                            {filename}
                                          </span>
                                          <a
                                            href={dataUrl}
                                            download={filename}
                                            className="btn btn-sm btn-outline-secondary ms-2"
                                            style={{
                                              fontSize: 11,
                                              padding: "1px 6px",
                                            }}
                                          >
                                            Local ⬇
                                          </a>
                                        </div>
                                      </div>
                                    );
                                  }
                                  if (contentType === "application/pdf") {
                                    return (
                                      <div
                                        key={idx}
                                        className="border rounded overflow-hidden"
                                        style={{ width: "100%" }}
                                      >
                                        <iframe
                                          src={dataUrl}
                                          style={{
                                            width: "100%",
                                            height: 320,
                                            border: "none",
                                          }}
                                          title={filename}
                                        />
                                        <div className="p-2 bg-light d-flex align-items-center justify-content-between">
                                          <span style={{ fontSize: 11 }}>
                                            📄 {filename}
                                          </span>
                                          <a
                                            href={dataUrl}
                                            download={filename}
                                            className="btn btn-sm btn-outline-secondary"
                                            style={{
                                              fontSize: 11,
                                              padding: "1px 8px",
                                            }}
                                          >
                                            Download
                                          </a>
                                        </div>
                                      </div>
                                    );
                                  }
                                  return (
                                    <a
                                      key={idx}
                                      href={dataUrl}
                                      download={filename}
                                      className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2"
                                      style={{ fontSize: 12 }}
                                    >
                                      📎 {filename}
                                      {att.size && (
                                        <span className="text-muted">
                                          ({formatBytes(att.size)})
                                        </span>
                                      )}
                                    </a>
                                  );
                                })}

                                {/* Quick action buttons */}
                                <div className="mt-3 d-flex gap-2">
                                  <button
                                    className="btn btn-sm btn-outline-secondary"
                                    style={{ fontSize: 12 }}
                                    onClick={() => handleCompose("reply")}
                                  >
                                    <FontAwesomeIcon
                                      icon={faReply}
                                      className="me-1"
                                    />
                                    Reply
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-secondary"
                                    style={{ fontSize: 12 }}
                                    onClick={() => handleCompose("replyAll")}
                                  >
                                    <FontAwesomeIcon
                                      icon={faReplyAll}
                                      className="me-1"
                                    />
                                    Reply All
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-secondary"
                                    style={{ fontSize: 12 }}
                                    onClick={() => handleCompose("forward")}
                                  >
                                    <FontAwesomeIcon
                                      icon={faMailForward}
                                      className="me-1"
                                    />
                                    Forward
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
